import { useState, useRef } from 'react';
import { X, Check, Upload, RefreshCw, Loader2, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import type {
  Story,
  ThumbnailRegenerationResponse,
  SetThumbnailResponse,
  UploadThumbnailResponse,
} from '@/types/story';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get full image URL
const getImageUrl = (url: string | undefined): string | null => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

const REGENERATIONS_LIMIT = 2;

interface ThumbnailPickerProps {
  story: Story;
  isOpen: boolean;
  onClose: () => void;
  onThumbnailChange: (newThumbnail: string, thumbnails: string[], activeIndex: number) => void;
}

type PickerState = 'selecting' | 'regenerating' | 'uploading';

const ThumbnailPicker: React.FC<ThumbnailPickerProps> = ({
  story,
  isOpen,
  onClose,
  onThumbnailChange,
}) => {
  const [state, setState] = useState<PickerState>('selecting');
  const [thumbnails, setThumbnails] = useState<string[]>(
    story.thumbnails && story.thumbnails.length > 0
      ? story.thumbnails
      : story.thumbnail
        ? [story.thumbnail]
        : []
  );
  const [activeIndex, setActiveIndex] = useState<number>(story.active_thumbnail_index || 0);
  const [regenerationCount, setRegenerationCount] = useState<number>(story.regeneration_count || 0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const regenerationsRemaining = REGENERATIONS_LIMIT - regenerationCount;
  const canRegenerate = regenerationsRemaining > 0;

  const handleSelectThumbnail = async (index: number) => {
    if (index === activeIndex) return;

    try {
      const response = await axios.post<SetThumbnailResponse>(
        `${API}/stories/${story.id}/set-thumbnail`,
        { thumbnail_index: index },
        { withCredentials: true }
      );

      setActiveIndex(index);
      onThumbnailChange(response.data.thumbnail, thumbnails, index);
      toast.success('Thumbnail updated');
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to update thumbnail';
      toast.error(message);
    }
  };

  const handleRegenerate = async () => {
    if (!canRegenerate) return;

    setState('regenerating');

    try {
      const response = await axios.post<ThumbnailRegenerationResponse>(
        `${API}/stories/${story.id}/regenerate-thumbnail`,
        {},
        { withCredentials: true }
      );

      const newThumbnails = response.data.thumbnails;
      const newActiveIndex = response.data.active_thumbnail_index;

      setThumbnails(newThumbnails);
      setActiveIndex(newActiveIndex);
      setRegenerationCount(response.data.regenerations_used);
      onThumbnailChange(response.data.thumbnail, newThumbnails, newActiveIndex);
      toast.success('New thumbnail generated');
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to generate thumbnail';
      toast.error(message);
    } finally {
      setState('selecting');
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please use JPG, PNG, WebP, or GIF.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setState('uploading');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post<UploadThumbnailResponse>(
        `${API}/stories/${story.id}/upload-thumbnail`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      const newThumbnails = response.data.thumbnails;
      const newActiveIndex = response.data.active_thumbnail_index;

      setThumbnails(newThumbnails);
      setActiveIndex(newActiveIndex);
      onThumbnailChange(response.data.thumbnail, newThumbnails, newActiveIndex);
      toast.success('Custom thumbnail uploaded');
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to upload thumbnail';
      toast.error(message);
    } finally {
      setState('selecting');
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClose = () => {
    if (state !== 'selecting') return;
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: 'rgba(0, 0, 0, 0.5)' }}
      onClick={handleClose}
    >
      <div
        className="rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        style={{
          background: 'var(--st-bg-surface)',
          border: '1px solid var(--st-border-light)',
          boxShadow: '0 12px 32px rgba(92, 64, 51, 0.18)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: 'var(--st-border-light)' }}
        >
          <div className="flex items-center gap-3">
            <ImageIcon className="w-5 h-5" style={{ color: 'var(--st-primary)' }} />
            <h2
              className="text-lg font-bold"
              style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
            >
              Choose Thumbnail
            </h2>
            <span
              className={`text-sm px-2 py-0.5 rounded-full ${canRegenerate ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'}`}
              style={{ fontFamily: 'Quicksand, sans-serif' }}
            >
              {regenerationsRemaining} regeneration{regenerationsRemaining !== 1 ? 's' : ''} left
            </span>
          </div>
          <button
            onClick={handleClose}
            disabled={state !== 'selecting'}
            className="p-2 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" style={{ color: 'var(--st-text-heading)' }} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {state === 'regenerating' && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Loader2
                className="w-12 h-12 animate-spin"
                style={{ color: 'var(--st-primary)' }}
              />
              <p
                className="text-lg font-semibold"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
              >
                Generating new thumbnail...
              </p>
              <p
                className="text-sm"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
              >
                This may take a moment
              </p>
            </div>
          )}

          {state === 'uploading' && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Loader2
                className="w-12 h-12 animate-spin"
                style={{ color: 'var(--st-primary)' }}
              />
              <p
                className="text-lg font-semibold"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
              >
                Uploading thumbnail...
              </p>
            </div>
          )}

          {state === 'selecting' && (
            <div className="space-y-4">
              {/* Thumbnail Grid */}
              <div className="grid grid-cols-3 gap-4">
                {thumbnails.map((thumb, index) => (
                  <button
                    key={index}
                    onClick={() => handleSelectThumbnail(index)}
                    className="relative aspect-video rounded-xl overflow-hidden transition-all hover:scale-[1.02]"
                    style={{
                      border: index === activeIndex
                        ? '3px solid var(--st-primary)'
                        : '1px solid var(--st-border-light)',
                      boxShadow: index === activeIndex
                        ? '0 6px 20px rgba(255, 140, 97, 0.4)'
                        : '0 2px 8px rgba(92, 64, 51, 0.08)',
                    }}
                  >
                    <img
                      src={getImageUrl(thumb) || ''}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {index === activeIndex && (
                      <div
                        className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center"
                        style={{ background: 'var(--st-primary)' }}
                      >
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div
                      className="absolute bottom-0 left-0 right-0 py-1 px-2 text-xs text-center"
                      style={{
                        background: 'rgba(255, 255, 255, 0.9)',
                        fontFamily: 'Quicksand, sans-serif',
                        color: 'var(--st-text-heading)',
                      }}
                    >
                      {index === 0 ? 'Original' : `Version ${index + 1}`}
                    </div>
                  </button>
                ))}

                {/* Add more placeholder if less than 3 */}
                {thumbnails.length < 3 && canRegenerate && (
                  <button
                    onClick={handleRegenerate}
                    className="aspect-video rounded-xl flex flex-col items-center justify-center gap-2 transition-all hover:scale-[1.02]"
                    style={{
                      border: '2px dashed var(--st-border-medium)',
                      background: 'var(--st-bg-muted)',
                    }}
                  >
                    <RefreshCw className="w-6 h-6" style={{ color: 'var(--st-primary)' }} />
                    <span
                      className="text-xs font-semibold"
                      style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
                    >
                      Generate New
                    </span>
                  </button>
                )}

                {/* Upload custom placeholder */}
                <button
                  onClick={handleUploadClick}
                  className="aspect-video rounded-xl flex flex-col items-center justify-center gap-2 transition-all hover:scale-[1.02]"
                  style={{
                    border: '2px dashed var(--st-border-medium)',
                    background: 'var(--st-bg-muted)',
                  }}
                >
                  <Upload className="w-6 h-6" style={{ color: 'var(--st-text-muted)' }} />
                  <span
                    className="text-xs font-semibold"
                    style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
                  >
                    Upload Custom
                  </span>
                </button>
              </div>

              {/* Help text */}
              <p
                className="text-xs text-center"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
              >
                Click a thumbnail to set it as your story cover, or generate/upload a new one
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-between px-6 py-4 border-t"
          style={{ borderColor: 'var(--st-border-light)' }}
        >
          <span
            className="text-xs"
            style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
          >
            {thumbnails.length} thumbnail{thumbnails.length !== 1 ? 's' : ''} saved
          </span>
          <div className="flex items-center gap-3">
            {canRegenerate && thumbnails.length >= 3 && (
              <button
                onClick={handleRegenerate}
                disabled={state !== 'selecting'}
                className="px-4 py-2.5 rounded-xl font-semibold text-sm transition-all hover:bg-orange-50 disabled:opacity-50"
                style={{
                  background: 'var(--st-bg-surface)',
                  border: '1px solid var(--st-border-light)',
                  color: 'var(--st-text-heading)',
                  fontFamily: 'Quicksand, sans-serif',
                }}
              >
                <span className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Generate New
                </span>
              </button>
            )}
            <button
              onClick={handleClose}
              disabled={state !== 'selecting'}
              className="px-6 py-2.5 rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
              style={{
                background: 'linear-gradient(135deg, var(--st-primary) 0%, var(--st-primary-hover) 100%)',
                color: '#FFF8F0',
                fontFamily: 'Quicksand, sans-serif'
              }}
            >
              Done
            </button>
          </div>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>
    </div>
  );
};

export default ThumbnailPicker;
